import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Target, TrendingUp, Shield } from 'lucide-react';
import '../styles/About.css';

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="about-page">
      {/* Navigation */}
      <nav className="landing-nav" data-testid="about-nav">
        <div className="nav-container">
          <div className="logo gradient-text" data-testid="about-logo">CNESS Hive</div>
          <div className="nav-links">
            <a href="/" data-testid="about-nav-home">Home</a>
            <a href="/about" data-testid="about-nav-about">About Us</a>
            <a href="/#contact" data-testid="about-nav-contact">Contact</a>
            <button className="gradient-btn" onClick={() => navigate('/login')} data-testid="about-login-btn">Login</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="about-hero fade-in" data-testid="about-hero">
        <h1 className="about-title">About CNESS Hive</h1>
        <p className="about-subtitle">Where productivity meets purpose</p>
      </section>

      {/* Mission Section */}
      <section className="about-content" data-testid="about-mission">
        <div className="content-container">
          <h2 className="gradient-text section-heading">Our Mission</h2>
          <p className="content-text">
            CNESS Hive is revolutionizing how teams track, manage, and optimize their productivity. We believe that sustainable success isn't just about working harder—it's about working smarter while maintaining well-being and balance.
          </p>
          <p className="content-text">
            Our platform empowers organizations to gain deep insights into team dynamics through the Six Dimensions framework: CNESS Tasks, Self-Care, R&D Learning, Social Media & Content Creation, Personal Time, and Inspired Work.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section" data-testid="about-values">
        <div className="values-container">
          <h2 className="section-heading">Our Core Values</h2>
          <div className="values-grid">
            <div className="value-card card slide-in" data-testid="value-card-transparency">
              <Shield size={48} className="value-icon" />
              <h3>Transparency</h3>
              <p>Clear insights and honest communication drive better decision-making across all levels of your organization.</p>
            </div>
            <div className="value-card card slide-in" data-testid="value-card-balance">
              <Target size={48} className="value-icon" />
              <h3>Balance</h3>
              <p>We champion work-life harmony, ensuring teams thrive both professionally and personally.</p>
            </div>
            <div className="value-card card slide-in" data-testid="value-card-growth">
              <TrendingUp size={48} className="value-icon" />
              <h3>Continuous Growth</h3>
              <p>Learning and development are at the heart of everything we do, fostering innovation and improvement.</p>
            </div>
            <div className="value-card card slide-in" data-testid="value-card-collaboration">
              <Users size={48} className="value-icon" />
              <h3>Collaboration</h3>
              <p>Strong teams are built on trust, communication, and shared goals—we enable that connection.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section" data-testid="about-story">
        <div className="story-container">
          <h2 className="gradient-text section-heading">Our Story</h2>
          <p className="story-text">
            CNESS Hive was born from a simple observation: traditional project management tools focus solely on tasks and deadlines, ignoring the human element. Teams were burning out, creativity was stifled, and managers lacked visibility into what truly drove productivity.
          </p>
          <p className="story-text">
            We set out to create something different—a platform that respects the multifaceted nature of work and life. By tracking six essential dimensions, we help teams understand not just what they're doing, but how they're spending their time and energy.
          </p>
          <p className="story-text">
            Today, CNESS Hive serves organizations of all sizes, from startups to enterprises, helping them build healthier, more productive, and more fulfilled teams.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" data-testid="about-cta">
        <div className="cta-content">
          <h2>Ready to Transform Your Team?</h2>
          <p>Join hundreds of organizations already using CNESS Hive to achieve better results.</p>
          <button className="gradient-btn cta-btn" onClick={() => navigate('/login')} data-testid="about-cta-btn">
            Get Started Now
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer" data-testid="about-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <h3 className="gradient-text">CNESS Hive</h3>
            <p>Empowering teams to achieve excellence through balanced productivity.</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Product</h4>
              <a href="#">Features</a>
              <a href="#">Pricing</a>
              <a href="#">Support</a>
            </div>
            <div className="footer-column">
              <h4>Company</h4>
              <a href="/about">About Us</a>
              <a href="#">Careers</a>
              <a href="#">Contact</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 CNESS Hive. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default About;